import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Search, ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  AccreditationTerm,
  TERM_BADGE_CLASSES,
  TERM_LABELS,
} from "@/components/Accreditation/types";

const ACCREDITATION_STATUS_BADGE: Record<string, string> = {
  accredited: "bg-green-100 text-green-800 border-green-300",
  expired: "bg-gray-100 text-gray-600 border-gray-300",
  revoked: "bg-red-100 text-red-800 border-red-300",
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  docs_requested: "bg-blue-100 text-blue-800 border-blue-300",
  under_review: "bg-[#0D2240] text-white border-transparent",
  approved: "bg-green-100 text-green-800 border-green-300",
  rejected: "bg-red-100 text-red-800 border-red-300",
  "not accredited": "bg-gray-100 text-gray-600 border-gray-300",
};

interface PressRegisterResult {
  full_name: string;
  outlet: string;
  country: string;
  position: string;
  accreditation_term: string;
  accreditation_status: string;
  pass_type: string | null;
  issued_at: string | null;
  valid_from: string | null;
  valid_to: string | null;
  photo_url: string | null;
}

function fmtDate(d: string | null | undefined) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function AccreditationCheck() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const [searchParams] = useSearchParams();
  const verifyParam = searchParams.get("verify");
  const [query, setQuery] = useState(verifyParam ?? "");
  const [submittedQuery, setSubmittedQuery] = useState(verifyParam ?? "");

  // Scanning a press pass QR code lands here with ?verify=<id> — search immediately.
  useEffect(() => {
    if (verifyParam) {
      setQuery(verifyParam);
      setSubmittedQuery(verifyParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyParam]);

  const { data: org } = useQuery({
    queryKey: ["accreditation_check_org", orgSlug],
    queryFn: async () => {
      const { data } = await supabase
        .from("organizations")
        .select("name, logo_url")
        .eq("slug", orgSlug)
        .single();
      return data;
    },
    enabled: !!orgSlug,
  });

  const {
    data: results,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["accreditation_check", orgSlug, submittedQuery],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("search_press_register" as any, {
        p_org_slug: orgSlug,
        p_query: submittedQuery,
      });
      if (error) throw error;
      return (data as PressRegisterResult[]) ?? [];
    },
    enabled: !!orgSlug && !!submittedQuery,
  });

  const tooShort = query.trim().length > 0 && query.trim().length < 3;

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (query.trim().length < 3) return;
    setSubmittedQuery(query.trim());
    refetch();
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border py-8">
        <div className="container mx-auto px-4 flex items-center gap-3">
          {org?.logo_url && (
            <img src={org.logo_url} alt="" className="w-10 h-10 rounded object-contain" />
          )}
          <div>
            <p className="text-justify text-xs uppercase tracking-wide text-muted-foreground">
              Accreditation check
            </p>
            <h1 className="text-xl font-semibold">
              {org?.name ?? "Institution"}
            </h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-xl">
        <p className="text-justify text-muted-foreground mb-6">
          Confirm whether a journalist is currently accredited with{" "}
          {org?.name ?? "this institution"}. Search by full name.
        </p>

        <form onSubmit={handleSearch} className="flex gap-2 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Full name…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={isFetching || query.trim().length < 3}>
            {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check"}
          </Button>
        </form>

        {tooShort && (
          <p className="text-justify text-xs text-muted-foreground -mt-6 mb-6">
            Enter at least 3 characters to search.
          </p>
        )}

        {submittedQuery && !isFetching && (
          <div className="space-y-4">
            {results && results.length > 0 ? (
              results.map((r, i) => (
                <Card key={`${r.full_name}-${i}`} className="overflow-hidden">
                  <CardContent className="pt-6 flex items-start gap-5">
                    {/* Photograph section */}
                    <div className="shrink-0 flex flex-col items-center gap-1.5">
                      <Avatar className="w-24 h-24 border-2" style={{ borderColor: "#006B3C" }}>
                        <AvatarImage
                          src={r.photo_url ?? org?.logo_url ?? undefined}
                          alt={r.full_name}
                          className={r.photo_url ? undefined : "object-contain p-3 bg-white"}
                        />
                        <AvatarFallback className="text-lg">
                          {r.full_name
                            .split(" ")
                            .map((p) => p[0])
                            .slice(0, 2)
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                      {!r.photo_url && (
                        <span className="text-[10px] text-muted-foreground text-center">
                          No photo on file
                        </span>
                      )}
                    </div>

                    <div className="flex-1 min-w-0 space-y-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <ShieldCheck className="w-5 h-5 text-green-600 shrink-0" />
                          <p className="text-justify font-semibold text-lg">{r.full_name}</p>
                        </div>
                        {r.position && (
                          <p className="text-justify text-sm text-muted-foreground ml-7">{r.position}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-y-2 text-sm">
                        <Field label="Outlet" value={r.outlet} />
                        <Field label="Country" value={r.country} />
                        <Field label="Issued" value={fmtDate(r.issued_at)} />
                        <Field label="Expires" value={fmtDate(r.valid_to)} />
                      </div>

                      <div className="flex items-center gap-2 pt-1 flex-wrap">
                        {r.accreditation_term && (
                          <Badge
                            variant="outline"
                            className={TERM_BADGE_CLASSES[r.accreditation_term as AccreditationTerm]}
                          >
                            {TERM_LABELS[r.accreditation_term as AccreditationTerm] ?? r.accreditation_term}
                          </Badge>
                        )}
                        {r.pass_type && (
                          <Badge variant="secondary" className="capitalize">
                            {r.pass_type} pass
                          </Badge>
                        )}
                        <Badge
                          variant="outline"
                          className={ACCREDITATION_STATUS_BADGE[r.accreditation_status] ?? ""}
                        >
                          {r.accreditation_status.replace(/_/g, " ")}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="pt-6 text-center space-y-2">
                  <ShieldAlert className="w-8 h-8 text-muted-foreground mx-auto" />
                  <p className="text-justify font-medium">No record found</p>
                  <p className="text-justify text-sm text-muted-foreground">
                    We couldn't find a matching accredited journalist. If you believe this is an
                    error, please contact the institution's communications office directly.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div>{value}</div>
    </div>
  );
}
